/**
 * The matter workspace's binding of the shared detail-page surface.
 *
 * Everything structural - the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what matters adds:
 * its config (`./layout`) and two typed hooks over the generic one.
 */

import useDetailSurface, {
  UseDetailSurfaceOptions,
  UseDetailSurfaceResult,
} from '../../shared/surface/useDetailSurface';
import { MATTER_SURFACE, MATTER_TABS, MatterPanelId, MatterTab } from './layout';
import { MatterItemKind, MatterReaderContent } from './MatterSurfaceContext';

export { MATTER_SURFACE, MATTER_TABS };
export type { MatterPanelId, MatterTab };

export type MatterWorkspace = UseDetailSurfaceResult<
  MatterPanelId,
  MatterItemKind,
  MatterReaderContent,
  MatterTab
>;

/**
 * The matter page's layout, selection, reader and tab state.
 *
 * Takes the matter id because the arrangement is remembered per matter - see
 * the note at the top of `./layout`.
 */
export const useMatterWorkspace = (
  matterId: string,
  options: Omit<UseDetailSurfaceOptions<MatterTab>, 'entityId'> = {}
): MatterWorkspace =>
  useDetailSurface<MatterPanelId, MatterItemKind, MatterReaderContent, MatterTab>(
    MATTER_SURFACE,
    { ...options, entityId: matterId }
  );

/** Just the panes, for a caller that owns its own tab and selection state. */
export const useMatterPanels = (matterId: string) =>
  useMatterWorkspace(matterId).panels;
